import { Box, Container, Flex, Text, Button, Loader, Center, Title, Tooltip } from "@mantine/core";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import AnonymousProfile from "@/components/shared/AnonymousCompanyProfile/AnonymousProfile";
import MatchedProfileView from "@/pages/profile/MatchedView";
import { IconEdit, IconSend } from "@tabler/icons-react";
import ApplyVerificationModal from "../profile-verification/ApplyVerificationModal";
import VerificationSubmittedModal from "@/pages/profile-verification/VerificationSubmittedModal";
import RejectionDetailsModal from "@/pages/profile-verification/VerificationRejectedModal";
import VerificationRejectedBanner from "../profile-verification/VerificationRejectedBanner";
import { useUserContext } from "@/context/UserContext";

export default function CompanyProfileView() {
  const navigate = useNavigate();
  const { userDetails, hasProfile } = useUserContext();
  const [activeView, setActiveView] = useState<"anonymized" | "matched">("anonymized");
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [verificationModalOpened, setVerificationModalOpened] = useState(false);
  const [verificationSubmittedModalOpened, setVerificationSubmittedModalOpened] = useState(false);
  const [rejectionModalOpened, setRejectionModalOpened] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [verificationData, setVerificationData] = useState<any>(null);

  const fetchCompanyData = useCallback(async () => {
    if (hasProfile === null) return;
    const companyId = userDetails?.company?.id;

    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const companyData = await trpc.company.getById.query({ companyId });
      setCompany(companyData);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  }, [hasProfile, userDetails?.company?.id]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  const handleApplyForVerification = async () => {
    if (!company?.id) return;

    setIsApplying(true);
    try {
      if (company.headquartersCountry === "India" && company.registrationIdType) {
        const type = company.registrationIdType;
        const ref = company.registrationNumber;

        if (!ref) {
          throw new Error("Registration number is missing.");
        }

        let result;
        if (type === "GST") {
          result = await trpc.gridlines.verifyGst.mutate({ gstin: ref });
        } else if (type === "UDM") {
          result = await trpc.gridlines.verifyUdm.mutate({ udyamReferenceNumber: ref });
        } else if (type === "CIN") {
          result = await trpc.gridlines.verifyCin.mutate({ companyId: ref }); // Gridlines verifyCin expects companyId
        }

        if (result && result.success) {
          setVerificationData(result.data);
          setVerificationModalOpened(true); // Reopen modal to show verification results
        }
      } else {
        await trpc.company.submitForReview.mutate({ companyId: company.id });
        setVerificationModalOpened(false);
        setVerificationSubmittedModalOpened(true);
      }
      await fetchCompanyData(); // Refresh the profile to capture SYSTEM_VERIFIED status or UNDER_REVIEW
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setIsApplying(false);
    }
  };

  const handleCloseVerificationModal = () => {
    setVerificationModalOpened(false);
    setTimeout(() => setVerificationData(null), 200);
  };

  if (loading) {
    return (
      <Center py="xl" mih="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!company) {
    return (
      <Container size="xl" py="xl">
        <Text>Company not found. Please create your company profile first.</Text>
        <Button mt="md" onClick={() => navigate("/create-profile")}>
          Go to Dashboard
        </Button>
      </Container>
    );
  }

  const companyStatus = company.status;
  const isUnderReview = companyStatus === "UNDER_REVIEW";
  const isSystemVerified = companyStatus === "SYSTEM_VERIFIED";
  const isRejected = companyStatus === "ACTION_REQUIRED";
  const canApplyForVerification = companyStatus === "DRAFT" || companyStatus === "ACTION_REQUIRED";

  const renderHeader = () => (
    <>
      <Box py="md" style={{ borderBottom: "1px solid #E5E7EB" }}>
        <Flex
          justify="space-between"
          pl={20}
          pr={20}
          align="flex-start"
          direction={{ base: "column", sm: "row" }}
          gap="sm"
        >
          <Title order={2} fw={400} fz={24}>
            Company Profile
          </Title>
          <Flex
            gap="sm"
            align={{ base: "stretch", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            w={{ base: "100%", sm: "auto" }}
          >
            <Tooltip
              label="Profile is under review and cannot be edited"
              disabled={!isUnderReview}
              withArrow
              position="bottom"
            >
              <Button
                variant="outline"
                size="sm"
                fz="sm"
                leftSection={<IconEdit size={18} />}
                onClick={() => navigate("/profile/edit")}
                disabled={isUnderReview || isSystemVerified}
                style={{ whiteSpace: "nowrap" }}
                w={{ base: "100%", sm: "auto" }}
              >
                Edit Profile
              </Button>
            </Tooltip>

            {canApplyForVerification && (
              <Button
                color="#34a853"
                size="sm"
                fz="sm"
                leftSection={<IconSend size={18} />}
                onClick={() => setVerificationModalOpened(true)}
                style={{ whiteSpace: "nowrap" }}
                w={{ base: "100%", sm: "auto" }}
              >
                Apply for Verification
              </Button>
            )}
          </Flex>
        </Flex>
      </Box>

      <Box py="md">
        <Container size="fluid">
          <Flex direction="column" gap="sm">
            <Flex justify="space-between" align="center" wrap="wrap" gap="sm">
              <Flex
                direction="column"
                gap="xs"
                style={{ backgroundColor: "#F3F4F6", padding: "8px", borderRadius: "10px", width: "fit-content" }}
              >
                <Flex gap="xs">
                  <Button
                    variant={activeView === "anonymized" ? "filled" : "light"}
                    color="gray"
                    size="md"
                    w={560}
                    onClick={() => setActiveView("anonymized")}
                    styles={{
                      root: {
                        width: "560px",
                        minHeight: "74px",
                        height: "auto",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        justifyContent: "center",
                        backgroundColor: activeView === "anonymized" ? "#2563EB" : "#FFFFFF",
                        color: activeView === "anonymized" ? "#FFFFFF" : "#1F2937",
                        fontWeight: 500,
                        border: "none",
                        boxShadow:
                          activeView === "anonymized"
                            ? "0px 1px 3px rgba(37, 99, 235, 0.35), 0px 1px 2px rgba(37, 99, 235, 0.25)"
                            : "none",
                        "&:hover": {
                          backgroundColor: activeView === "anonymized" ? "#1D4ED8" : "#F9FAFB",
                        },
                      },
                    }}
                  >
                    <Flex direction="column" gap={2} align="center">
                      <Text fw={500} size="sm" c={activeView === "anonymized" ? "#FFFFFF" : "#1F2937"}>
                        Anonymized View
                      </Text>
                      <Text size="xs" c={activeView === "anonymized" ? "rgba(255,255,255,0.92)" : "#6B7280"}>
                        How your company profile appears to others before revealing your identity.
                      </Text>
                    </Flex>
                  </Button>
                  <Button
                    variant={activeView === "matched" ? "filled" : "light"}
                    color="gray"
                    size="md"
                    w={560}
                    onClick={() => setActiveView("matched")}
                    styles={{
                      root: {
                        width: "560px",
                        minHeight: "74px",
                        height: "auto",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        justifyContent: "center",
                        backgroundColor: activeView === "matched" ? "#2563EB" : "#FFFFFF",
                        color: activeView === "matched" ? "#FFFFFF" : "#1F2937",
                        fontWeight: 500,
                        border: "none",
                        boxShadow:
                          activeView === "matched"
                            ? "0px 1px 3px rgba(37, 99, 235, 0.35), 0px 1px 2px rgba(37, 99, 235, 0.25)"
                            : "none",
                        "&:hover": {
                          backgroundColor: activeView === "matched" ? "#1D4ED8" : "#F9FAFB",
                        },
                      },
                    }}
                  >
                    <Flex direction="column" gap={2} align="center">
                      <Text fw={500} size="sm" c={activeView === "matched" ? "#FFFFFF" : "#1F2937"}>
                        Matched View
                      </Text>
                      <Text size="xs" c={activeView === "matched" ? "rgba(255,255,255,0.92)" : "#6B7280"}>
                        How your full company profile is shared after a mutual match is established.
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Container>
      </Box>

      {isRejected && (
        <Container size="fluid" mb="md">
          <VerificationRejectedBanner onKnowMore={() => setRejectionModalOpened(true)} />
        </Container>
      )}
    </>
  );

  return (
    <Box>
      {renderHeader()}

      {activeView === "anonymized" ? (
        <AnonymousProfile pageID="MY_PROFILE_PREVIEW" companyData={company} />
      ) : (
        <MatchedProfileView company={company} containerSize="fluid" isOwnProfile={true} />
      )}

      <ApplyVerificationModal
        opened={verificationModalOpened}
        onClose={handleCloseVerificationModal}
        onApply={handleApplyForVerification}
        isLoading={isApplying}
        verificationData={verificationData}
      />

      <VerificationSubmittedModal
        opened={verificationSubmittedModalOpened}
        onClose={() => setVerificationSubmittedModalOpened(false)}
      />

      <RejectionDetailsModal
        opened={rejectionModalOpened}
        onClose={() => setRejectionModalOpened(false)}
        adminComments={company?.adminComments}
      />
    </Box>
  );
}
